import React, { useEffect, useRef, useState, useContext } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { 
  addServiceRegion, 
  deleteServiceRegion, 
  updateServiceRegion, 
} from "requests/serviceRegions";
import {
  addCountyToServiceRegion,
  // removeCountyFromServiceRegion,
  getCountiesForServiceRegion,
} from "requests/region/index.tsx";
import { SERVICE_REGION_EDIT, SERVICE_REGION_DELETE } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import MGrid, { ACTION_ADD, 
  // ACTION_DELETE, ACTION_EDIT, ACTION_CANCEL, 
  FILTER_TYPE_TEXT } from "components/Shared/MGrid.tsx";
import { MCard, MSelect } from "components/Shared";
import { getAllCounties } from "actions/region";
import { allCountiesSelector } from "selectors/menuItems";
import { ServiceTypeEnum } from "typeConstants/ServiceTypeEnum";
import { DialogContext } from "components/Shared/DialogProvider.tsx";

// Note: the feature to delete existing counties is not currently supported by the back end

const ServiceRegionDetails = props => {
  const [counties, setCounties] = useState([]);
  const [fetchingData, setFetchingData ] = useState(false);
  const [editingCounties, setEditingCounties] = useState(false);
  const [selectedRowIndexes, setSelectedRowIndexes] = useState([]);
  const [newServiceRegionId, setNewServiceRegionId] = useState();
  const serviceRegionName = useRef(props.selectedServiceRegionName ?? "");
  const mounted = useRef(true);
  const selectedCountyId = useRef();
  const dialog = useContext(DialogContext);

  const creatingServiceRegion = !props.selectedServiceRegionId && !newServiceRegionId;

  useEffect(() => {
    if (props.selectedServiceRegionId) {
      setFetchingData(true);
      getCountiesForSelected();
      serviceRegionName.current = props.selectedServiceRegionName;
      if (selectedRowIndexes) setSelectedRowIndexes([]);
      if (editingCounties) setEditingCounties(false);
      if (newServiceRegionId) setNewServiceRegionId();
    }
  }, [props.selectedServiceRegionId]);

  useEffect(() => {
    if (props.canEdit && !props.allCounties.length) {
      props.getAllCounties();
    }
    return () => mounted.current = false;
  }, []);

  const handleCounties = response => {
    setCounties(response);
    setFetchingData(false);
  };

  const onEditFinished = data => {
    let updatedName = data?.[0]?.value;
    serviceRegionName.current = updatedName;
    setFetchingData(true);
    if (creatingServiceRegion) {
      addServiceRegion({
        name: updatedName,
        type: ServiceTypeEnum.Inspector,
      }, addRegionCallback, requestCallback, mounted);
    } else {
      updateServiceRegion({ 
        id: props.selectedServiceRegionId,
        name: updatedName,
       }, requestCallback, requestCallback, mounted);
    }
  };

  const getCountiesForSelected = () => getCountiesForServiceRegion(getServiceRegionId(), handleCounties, mounted);
  const onRowSelected = indexes => setSelectedRowIndexes(indexes);
  const onCountyChange = option => selectedCountyId.current = option.value;
  const onAddCounty = () => dialog.show("Add County to MSA", dialogContent, dialogActions, dialogProps);
  const getServiceRegionId = () => props.selectedServiceRegionId ?? newServiceRegionId;
  
  const requestCallback = () => setFetchingData(false);
  const addRegionCallback = id => { 
    setNewServiceRegionId(id); 
    setFetchingData(false);
    props.refreshServiceRegions();
  };
  // const deleteCountyCallback = () => { 
  //   setSelectedRowIndexes([]);
  //   setEditingCounties(false);
  //   getCountiesForSelected(); 
  // };
  const deleteServiceRegionCallback = () => {
    props.refreshServiceRegions();
    props.closePanel();
  };
  const handleAddCounty = () => {
    const addCountyCallback = () => getCountiesForSelected();
    setFetchingData(true);
    addCountyToServiceRegion(getServiceRegionId(), selectedCountyId.current, addCountyCallback, requestCallback, mounted);
  };

  // const onDeleteCounties = () => {
  //   setFetchingData(true);
  //   selectedRowIndexes.forEach((value, i) => {
  //     let countyId = counties[value].id;
  //     // If this is the last county to delete, reload counties
  //     let successCallback = i === selectedRowIndexes.length - 1 ? deleteCountyCallback : () => {};
  //     removeCountyFromServiceRegion(getServiceRegionId(), countyId, successCallback, requestCallback, mounted);
  //   });
  // };

  const onConfirmDelete = () => {
    deleteServiceRegion(getServiceRegionId(), deleteServiceRegionCallback, requestCallback, mounted);
  };

  const onDeleteServiceRegion = () => {
    if (counties.length) {
      // Service regions with assigned counties/zip codes cannot be deleted
      dialog.showOk("Unable to Delete", "Please remove all counties from MSA before deleting.");
    } else {
      dialog.showYesNo(
        "Delete MSA",
        "Are you sure you want to delete this MSA? This action cannot be undone.",
        onConfirmDelete,
        null,
        "Yes, Delete",
        "No, Cancel"
      );
    }
  };

  const dialogContent = (
    <MSelect
      className={"mselect-service-region-county"}
      key={"mselect-service-region-county"}
      id={"mselect-service-region-county"}
      options={props.allCounties}
      placeholder={"Select county..."}
      label={"County"}
      onChange={onCountyChange}
      getMenuPortalTarget={() => document.querySelector("mselect-service-region-county")}
    />);

  const dialogActions = [{
      key: "service-region-add-county",
      label: "Add County",
      isPrimary: true,
      onClick: handleAddCounty,
      disabled: !selectedCountyId.current
    }, {
      key: "cancel-service-region-add-county",
      label: "Cancel",
    },
  ];
  const dialogProps = { className: "add-service-region-county-dialog"};

  const getActionButtons = () => {
    let actions = [];
    if (props.canEdit) {
      actions.push({ 
        code: ACTION_ADD, 
        onClick: onAddCounty
      });
      // if (editingCounties) {
      //   actions.push({ 
      //     code: ACTION_DELETE, 
      //     onClick: onDeleteCounties
      //   }, { 
      //     code: ACTION_CANCEL, 
      //     onClick: () => setEditingCounties(false)
      //   });
      // } else {
      //   actions.push({ 
      //     code: ACTION_EDIT, 
      //     onClick: () => setEditingCounties(true)
      //   });
      // } 
    }
    return actions;
  };
  
  const cardFields = [
    { key: "name", label: "Name", value: serviceRegionName.current, editable: props.canEdit }
  ];

  const countyColumns = [
    {
      key: "name",
      name: "Name",
      width: 300,
      dataSource: "name",
      resizable: false,
      filterType: FILTER_TYPE_TEXT
    },
    {
      key: "fips",
      name: "FIPS",
      width: 100,
      dataSource: "fips",
      resizable: false,
      filterType: FILTER_TYPE_TEXT
    },
    {
      key: "state",
      name: "State",
      width: 75,
      dataSource: "state",
      resizable: false,
      filterType: FILTER_TYPE_TEXT
    }
  ];

  const cardTitle = props.selectedServiceRegionId || newServiceRegionId
    ? props.canEdit
      ? "Edit MSA"
      : "MSA"
    : "Add MSA";

  return (
    <Grid container spacing={2} className="service-region-details-grid">
      <Grid item xs={12}>
        <MCard 
          className="service-region-mcard"
          title={cardTitle}
          isEditable={props.canEdit}
          onEditFinished={onEditFinished}
          singleColumn
          startInEditMode={creatingServiceRegion}
          fields={cardFields}
          isFetching={fetchingData}
        >
          {(props.selectedServiceRegionId || newServiceRegionId) ? <>
            <div>
              <MGrid 
                headerText="Counties"
                columnDefinitions={countyColumns}
                data={counties}
                isFetchingData={props.fetchingAllCounties || fetchingData}
                displayNumOfItemsFound
                actions={props.canEdit ? getActionButtons() : null}
                allowRowSelection={editingCounties}
                onRowSelectionChanged={onRowSelected}
                selectedRows={selectedRowIndexes}
              />
            </div>
            {props.canDelete && <Button
              onClick={onDeleteServiceRegion}
            >
              Delete MSA
            </Button>}
          </> : <p>Please name the new MSA and save before adding counties</p>}
        </MCard>
      </Grid>
    </Grid>
  );
};

ServiceRegionDetails.propTypes = {
  selectedServiceRegionName: PropTypes.string,
  selectedServiceRegionId: PropTypes.number,
  closePanel: PropTypes.func,
  refreshServiceRegions: PropTypes.func,
  // Redux
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  allCounties: PropTypes.array,
  fetchingAllCounties: PropTypes.bool,
};

export default connect(
  state => ({
    canEdit: userHasRight(SERVICE_REGION_EDIT)(state),
    canDelete: userHasRight(SERVICE_REGION_DELETE)(state),
    allCounties: allCountiesSelector(state),
    fetchingAllCounties: state.region.isFetchingAllCounties,
  }),
  dispatch => ({
    getAllCounties() {
      dispatch(getAllCounties());
    },
  })
)(ServiceRegionDetails);